import type { ActionDefinition } from '../../types';
import type { ActionHandler, ActionContext } from '../../registry/ActionRegistry';

/**
 * Wait action definition
 * Simple delay without waiting for an element
 */
export interface WaitAction extends ActionDefinition {
  type: 'wait';
  /** Duration to wait in milliseconds */
  value: number;
}

/**
 * Handler for simple wait/delay actions
 * Pauses execution for a specified duration
 */
export class WaitActionHandler implements ActionHandler<WaitAction> {
  async execute(action: WaitAction, context: ActionContext): Promise<void> {
    const duration = typeof action.value === 'number' ? action.value : 0;

    if (duration <= 0) {
      return; // No wait needed
    }

    await context.timerAdapter.delay(duration);
  }

  validate(action: WaitAction): boolean {
    return typeof action.value === 'number' && action.value >= 0;
  }
}

/**
 * Wait for element action definition
 */
export interface WaitForElementAction extends ActionDefinition {
  type: 'wait-for';
  targetSelector: string;
  /** Condition function to check (optional) */
  condition?: () => boolean | Promise<boolean>;
  /** Timeout in milliseconds */
  timeout?: number;
}

/**
 * Handler for waiting until an element appears or a condition is met
 */
export class WaitForElementActionHandler implements ActionHandler<WaitForElementAction> {
  async execute(action: WaitForElementAction, context: ActionContext): Promise<void> {
    const { targetSelector, condition, timeout = 30000 } = action;
    const startTime = Date.now();

    // Wait for element with remaining timeout budget
    const element = await context.domAdapter.waitForElement(targetSelector, timeout);
    if (!element) {
      throw new Error(`Element not found within timeout: ${targetSelector}`);
    }

    // If condition is provided, poll until it's true with remaining time
    if (condition) {
      const elapsed = Date.now() - startTime;
      const remainingTimeout = Math.max(0, timeout - elapsed);
      const pollInterval = 100;
      const conditionStartTime = Date.now();

      // Use the remaining timeout budget for condition polling
      while (Date.now() - conditionStartTime < remainingTimeout) {
        try {
          const result = await condition();
          if (result) {
            return; // Condition met
          }
        } catch (error) {
          // Continue polling even if condition throws
          console.warn('Condition check failed:', error);
        }

        await context.timerAdapter.delay(pollInterval);
      }

      throw new Error('Condition not met within timeout');
    }
  }

  validate(action: WaitForElementAction): boolean {
    return Boolean(action.targetSelector);
  }
}
